import { Menu as MuiMenu, MenuItem as MuiMenuItem, MenuProps as MuiMenuProps } from '@mui/material';
import React from 'react';

export interface MenuOption {
  label: string;
  value: string | number;
  disabled?: boolean;
  domSpm?: { 'data-spm': string };
}

export interface MenuProps extends Omit<MuiMenuProps, 'children' | 'onSelect'> {
  options: MenuOption[];
  selectedValue?: string | number;
  onSelect?: (value: string | number, option: MenuOption) => void;
  menuItemStyles?: React.CSSProperties;
  selectedItemStyles?: React.CSSProperties;
  minWidth?: number;
  customMenuStyles?: React.CSSProperties;
}

export default function Menu({
  options,
  selectedValue,
  onSelect,
  menuItemStyles = {},
  selectedItemStyles = {},
  minWidth = 180,
  customMenuStyles = {},
  sx,
  ...menuProps
}: MenuProps) {
  const handleSelect = (option: MenuOption) => {
    if (option.disabled) return;
    onSelect?.(option.value, option);
  };

  const defaultMenuStyles = {
    '& .MuiPaper-root': {
      minWidth,
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
      borderRadius: '8px',
      padding: '10px',
      ...customMenuStyles,
    },
    '& .MuiPopover-paper': {
      maxHeight: '400px',
    },
  };

  const defaultMenuItemStyles = {
    fontSize: '14px',
    height: '36px',
    cursor: 'pointer',
    ...menuItemStyles,
  };

  const defaultSelectedItemStyles = {
    backgroundColor: '#ffffff',
    color: 'primary.main',
    '&:hover': {
      backgroundColor: '#E8ECFF',
    },
    ...selectedItemStyles,
  };

  return (
    <MuiMenu
      {...menuProps}
      sx={{
        ...defaultMenuStyles,
        ...sx,
      }}
    >
      {options.map((option) => {
        const isSelected = option.value === selectedValue;
        return (
          <MuiMenuItem
            {...option.domSpm}
            key={option.value}
            onClick={() => handleSelect(option)}
            selected={isSelected}
            disabled={option.disabled}
            sx={{
              ...defaultMenuItemStyles,
              ...(isSelected && {
                ...defaultSelectedItemStyles,
                '&.Mui-selected': defaultSelectedItemStyles,
                '&.Mui-selected:hover': defaultSelectedItemStyles['&:hover'],
              }),
            }}
          >
            {option.label}
          </MuiMenuItem>
        );
      })}
    </MuiMenu>
  );
}
